import { type JSX, FC, useEffect, useMemo } from 'react';
import { Provider, useUnit } from 'effector-react/scope';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import { withControl } from '@features/effector-form/hoc/withControl';
import {
  $data,
  $pending,
  getList as getListEvent,
  createScope,
  search as searchEvent,
} from '../model';
import type { Object, GetList } from '../types';

export const ObjectsWithSearchControl: FC<any> = ({
  value,
  setValue,
  error,
  label,
  required,
  readOnly,
  disabled,
  isError,
  fastFilters = {},
}) => {
  const [data, pending, getList, search] = useUnit([
    $data,
    $pending,
    getListEvent,
    searchEvent,
  ]);
  const theme = useTheme();
  const memoFastFilters = useMemo(() => fastFilters, [JSON.stringify(fastFilters)]);

  useEffect(() => {
    getList({ fastFilters: memoFastFilters });
  }, [memoFastFilters]);

  const findedValue = !data
    ? []
    : data.filter(
        (item: Object) =>
          value.map((i: Object) => (typeof i === 'object' ? i.id : i)).indexOf(item.id) >
          -1
      );

  const handleChange = (_: unknown, newValue: any) =>
    setValue(newValue.map((item: Object) => item));

  const handleInputChange = (_: unknown, inputValue: string, reason: string) => {
    if (reason === 'input') {
      return search({ fastFilters: memoFastFilters, search: inputValue });
    }

    if (reason === 'clear' || (reason === 'blur' && !value)) {
      return search({ fastFilters: memoFastFilters });
    }
  };

  return (
    <FormControl required={required} disabled={disabled}>
      {label && <FormLabel error={isError}>{label}</FormLabel>}
      <Autocomplete
        multiple
        loading={pending}
        value={findedValue}
        options={!data ? [] : data}
        onChange={handleChange}
        filterOptions={(options) => options}
        onInputChange={handleInputChange}
        slotProps={{
          listbox: {
            component: Listbox,
          },
        }}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        readOnly={readOnly}
        disabled={disabled}
        renderOption={({ key, ...optionProps }, option, { inputValue }) => {
          const matches = match(option.label, inputValue, { insideWords: true });
          const parts = parse(option.label, matches);

          return (
            <li key={key} {...optionProps}>
              <div>
                {parts.map(
                  (part: { text: string; highlight: boolean }, index: number) => (
                    <span
                      key={index}
                      style={{
                        color: part.highlight
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      }}
                    >
                      {part.text}
                    </span>
                  )
                )}
              </div>
            </li>
          );
        }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export const ObjectsWithSearchControlWrapper = withControl<GetList>(
  ({ name, ...props }) => {
    const scope = createScope(name);

    return (
      <Provider value={scope}>
        <ObjectsWithSearchControl {...props} />
      </Provider>
    );
  }
);
